import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLeaderboard, useSeasons } from '../api/hooks';
import { ErrorView, EmptyView } from '../components/StateViews';
import { BlueHeader } from '../components/ScreenShell';
import { haptics } from '../telegram';
import { track } from '../analytics';
import type { LeaderboardRow } from '../api/types';

function Row({ r, label }: { r: LeaderboardRow; label?: string }) {
  // A fresh season ranks everyone #1 with 0 points — no crowns until scored.
  const leader = r.rank === 1 && r.points > 0;
  return (
    <Link
      className={`lb-row ${r.isMe ? 'me' : ''} ${leader ? 'first' : ''}`}
      to={`/players/${r.playerId}`}
      onClick={() => track('player_open', { playerId: r.playerId, from: 'leaderboard' })}
    >
      <div className="lb-rank">{r.rank}</div>
      <div className="lb-name">{label ?? r.name}</div>
      <div className="lb-points">{r.points}</div>
    </Link>
  );
}

/** Плитки сезонов: только реально сыгранные (список приходит из API),
 *  новый сезон появляется сам, как только в нём создана первая игра. */
export function LeaderboardScreen() {
  const seasons = useSeasons();
  const [seasonId, setSeasonId] = useState<number | null>(null);

  // Ascending by absolute id — the tile shows that id.
  const list = useMemo(
    () => [...(seasons.data ?? [])].sort((a, b) => a.id - b.id),
    [seasons.data],
  );

  useEffect(() => {
    if (seasonId === null && seasons.data && seasons.data.length > 0) {
      const active = seasons.data.find((s) => s.isActive) ?? seasons.data[0];
      setSeasonId(active.id);
    }
  }, [seasons.data, seasonId]);

  const lb = useLeaderboard(seasonId);

  if (seasons.isError) return <ErrorView message={(seasons.error as Error)?.message} />;

  const pickSeason = (id: number) => {
    if (id === seasonId) return;
    haptics.tap();
    track('season_switch', { seasonId: id });
    setSeasonId(id);
  };

  return (
    <div>
      <BlueHeader mascot="tr">
        <div className="bhead-title">Таблица лидеров</div>

        <div className="season-count">СЕЗОНОВ: {list.length || '…'}</div>

        <div className="season-tiles">
          {list.map((s) => (
            <button
              key={s.id}
              className={`tile ${s.id === seasonId ? 'tile--current' : ''}`}
              onClick={() => pickSeason(s.id)}
            >
              {s.id}
            </button>
          ))}
        </div>
      </BlueHeader>

      <div className="content">
        {seasonId === null ? (
          <LeaderboardSkeleton />
        ) : (
          <>
            <div className="lb-head">
              <span>Место · Игрок</span>
              <span>Очки</span>
            </div>

            {lb.isLoading ? (
              <LeaderboardSkeleton />
            ) : lb.isError ? (
              <ErrorView message={(lb.error as Error)?.message} />
            ) : !lb.data || lb.data.rows.length === 0 ? (
              <EmptyView message="В этом сезоне ещё нет результатов" />
            ) : (
              <>
                <div className="lb-list">
                  {lb.data.rows.map((r) => (
                    <Row key={r.playerId} r={r} />
                  ))}
                </div>
                {lb.data.me && (
                  // key: Telegram-вебвью (WebKit) перерисовывает sticky-слой лениво —
                  // без пересоздания узла место обновлялось только при прокрутке.
                  <div className="me-sticky you-row" key={`me-${seasonId}`}>
                    <Row r={lb.data.me} label={`Вы · ${lb.data.me.name}`} />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** Shimmer placeholders (handoff: skeleton rows while loading). */
function LeaderboardSkeleton() {
  return (
    <div>
      {Array.from({ length: 8 }, (_, i) => (
        <div key={i} className="skeleton" style={{ height: 38, borderRadius: 10, marginBottom: 8 }} />
      ))}
    </div>
  );
}
